// The crew marker as a DOM element, for the three mapbox-gl maps on web
// (producer live tracking, the dashboard preview card, and the crew map).
//
// mapbox-gl markers take a raw HTMLElement, so this can't be a React
// component — but it doesn't need to be imperative *and* duplicated.
// Before this module the same hand-rolled div was built in three places
// and had already drifted apart (different sizes, borders and hover
// rules). Here it's built once; every number and colour comes from
// @produce/shared so the crew app's native marker stays identical too.
//
// Split in two on purpose: create* builds the skeleton once, update*
// applies state. The producer map reconciles markers in place on every
// 5s poll, and rebuilding the DOM each time made them blink.

import {
  CREW_MARKER_BASE_SIZE,
  CREW_MARKER_COLORS,
  crewMarkerGeometry,
  crewOwnColor,
  ringDash,
} from "@produce/shared";

/** Everything the marker needs to draw itself. */
export interface CrewMarkerVisualState {
  /** Shown when there is no profile photo. */
  initials: string;
  /** Signed URL of the crew member's profile photo, when they have one. */
  photoUrl?: string | null;
  /** The crew member's personal colour; falls back to the shared blue. */
  ownColor?: string | null;
  /**
   * How far along their current assignment is (0 = just started,
   * 1 = done). null means "no assignment right now" — the ring then
   * shows as an empty grey track.
   */
  progress?: number | null;
  /** Staleness fade, see markerOpacityForAge. Defaults to fully solid. */
  opacity?: number;
  /** Draws the white selection halo and scales the marker up. */
  isSelected?: boolean;
  /** Producer-only highlight for crew away from their assigned stage. */
  isFarFromStage?: boolean;
  /** Set false for the small preview marker, which has no pointer. */
  showTail?: boolean;
}

const SVG_NS = "http://www.w3.org/2000/svg";

const CLASS = {
  root: "crew-marker",
  inner: "crew-marker-inner",
  track: "crew-marker-track",
  progress: "crew-marker-progress",
  own: "crew-marker-own",
  disc: "crew-marker-disc",
  photo: "crew-marker-photo",
  label: "crew-marker-label",
  tail: "crew-marker-tail",
} as const;

/** Marker size in px, keyed by where it is drawn. */
export const CREW_MARKER_SIZE = {
  /** Full maps — producer live tracking and the crew map. */
  map: CREW_MARKER_BASE_SIZE,
  /** The dashboard preview card, which is a fraction of the screen. */
  preview: 32,
} as const;

const circle = (className: string): SVGCircleElement => {
  const el = document.createElementNS(SVG_NS, "circle");
  el.setAttribute("class", className);
  return el;
};

/**
 * Build an empty marker of `size` px. Call updateCrewMarkerElement to
 * put a crew member in it.
 *
 * The returned element is meant to be handed to mapbox-gl with
 * `anchor: "bottom"`, so the pointer at the bottom lands on the actual
 * coordinate.
 */
export function createCrewMarkerElement(size: number = CREW_MARKER_SIZE.map): HTMLDivElement {
  const g = crewMarkerGeometry(size);

  const root = document.createElement("div");
  root.className = CLASS.root;
  // Absolute, pinned to the container's origin — exactly what mapbox-gl's own
  // `.mapboxgl-marker` rule does, and what its positioning assumes.
  //
  // This used to be `position: relative`, and an inline style beats mapbox's
  // stylesheet. The element then sat in the canvas container's normal FLOW,
  // so mapbox's `transform: translate(x, y)` was applied from wherever the
  // layout happened to put it — a different, stacking offset for every
  // marker. Crew appeared near the right place at one zoom and scattered off
  // the site at another, because the flow offsets don't scale with the map.
  // Setting it here rather than leaning on mapbox's class also keeps `inner`
  // (absolute) resolving against this element.
  root.style.position = "absolute";
  root.style.top = "0";
  root.style.left = "0";
  root.style.width = `${g.size}px`;
  root.style.height = `${g.totalHeight}px`;
  root.style.cursor = "pointer";

  const inner = document.createElement("div");
  inner.className = CLASS.inner;
  inner.style.position = "absolute";
  inner.style.top = "0";
  inner.style.left = "0";
  inner.style.width = `${g.size}px`;
  inner.style.height = `${g.size}px`;
  inner.style.borderRadius = "50%";
  inner.style.transition = "transform 0.2s, box-shadow 0.2s";

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", `${g.size}`);
  svg.setAttribute("height", `${g.size}`);
  svg.setAttribute("viewBox", `0 0 ${g.size} ${g.size}`);
  svg.setAttribute("aria-hidden", "true");
  svg.style.position = "absolute";
  svg.style.top = "0";
  svg.style.left = "0";

  const track = circle(CLASS.track);
  track.setAttribute("cx", `${g.center}`);
  track.setAttribute("cy", `${g.center}`);
  track.setAttribute("r", `${g.progressRadius}`);
  track.setAttribute("fill", "none");
  track.setAttribute("stroke", CREW_MARKER_COLORS.track);
  track.setAttribute("stroke-width", `${g.progressWidth}`);

  // Rotated so the arc starts at 12 o'clock and fills clockwise.
  const progress = circle(CLASS.progress);
  progress.setAttribute("cx", `${g.center}`);
  progress.setAttribute("cy", `${g.center}`);
  progress.setAttribute("r", `${g.progressRadius}`);
  progress.setAttribute("fill", "none");
  progress.setAttribute("stroke", CREW_MARKER_COLORS.progress);
  progress.setAttribute("stroke-width", `${g.progressWidth}`);
  progress.setAttribute("stroke-linecap", "round");
  progress.setAttribute("transform", `rotate(-90 ${g.center} ${g.center})`);
  progress.style.transition = "stroke-dashoffset 0.3s linear";

  const own = circle(CLASS.own);
  own.setAttribute("cx", `${g.center}`);
  own.setAttribute("cy", `${g.center}`);
  own.setAttribute("r", `${g.ownRadius}`);
  own.setAttribute("fill", "none");
  own.setAttribute("stroke-width", `${g.ownWidth}`);

  const disc = circle(CLASS.disc);
  disc.setAttribute("cx", `${g.center}`);
  disc.setAttribute("cy", `${g.center}`);
  disc.setAttribute("r", `${g.discRadius}`);
  disc.setAttribute("fill", CREW_MARKER_COLORS.disc);

  svg.append(track, progress, own, disc);

  // Photo sits on top of the disc and covers it exactly; the label sits
  // under it and shows through when there is no photo.
  const photo = document.createElement("div");
  photo.className = CLASS.photo;
  photo.style.position = "absolute";
  photo.style.top = `${g.photoOffset}px`;
  photo.style.left = `${g.photoOffset}px`;
  photo.style.width = `${g.photoSize}px`;
  photo.style.height = `${g.photoSize}px`;
  photo.style.borderRadius = "50%";
  photo.style.backgroundSize = "cover";
  photo.style.backgroundPosition = "center";
  photo.style.display = "none";

  const label = document.createElement("div");
  label.className = CLASS.label;
  label.style.position = "absolute";
  label.style.top = "0";
  label.style.left = "0";
  label.style.width = `${g.size}px`;
  label.style.height = `${g.size}px`;
  label.style.display = "flex";
  label.style.alignItems = "center";
  label.style.justifyContent = "center";
  label.style.color = CREW_MARKER_COLORS.label;
  label.style.fontWeight = "700";
  label.style.fontSize = `${g.fontSize}px`;
  label.style.lineHeight = "1";
  label.style.pointerEvents = "none";

  inner.append(svg, photo, label);

  const tail = document.createElement("div");
  tail.className = CLASS.tail;
  tail.style.position = "absolute";
  tail.style.top = `${g.size}px`;
  tail.style.left = "50%";
  tail.style.transform = "translateX(-50%)";
  tail.style.width = "0";
  tail.style.height = "0";
  tail.style.borderLeft = `${g.tailWidth / 2}px solid transparent`;
  tail.style.borderRight = `${g.tailWidth / 2}px solid transparent`;
  tail.style.borderTopStyle = "solid";
  tail.style.borderTopWidth = `${g.tailHeight}px`;

  root.append(inner, tail);
  return root;
}

/**
 * Apply a crew member's state to a marker built by
 * createCrewMarkerElement. Safe to call repeatedly on the same element —
 * that's how the producer map keeps markers from blinking on each poll.
 */
export function updateCrewMarkerElement(root: HTMLElement, state: CrewMarkerVisualState): void {
  const inner = root.querySelector<HTMLElement>(`.${CLASS.inner}`);
  const progress = root.querySelector<SVGCircleElement>(`.${CLASS.progress}`);
  const own = root.querySelector<SVGCircleElement>(`.${CLASS.own}`);
  const photo = root.querySelector<HTMLElement>(`.${CLASS.photo}`);
  const label = root.querySelector<HTMLElement>(`.${CLASS.label}`);
  const tail = root.querySelector<HTMLElement>(`.${CLASS.tail}`);
  if (!inner || !progress || !own || !photo || !label || !tail) return;

  const ownColor = crewOwnColor(state.ownColor);
  own.setAttribute("stroke", ownColor);
  tail.style.borderTopColor = ownColor;
  tail.style.display = state.showTail === false ? "none" : "block";

  // No current assignment → hide the arc entirely and leave the grey
  // track, which is what "this person is free right now" looks like.
  const radius = Number(progress.getAttribute("r")) || 0;
  const hasProgress = state.progress != null;
  const { circumference, dashOffset } = ringDash(radius, state.progress ?? 0);
  progress.style.display = hasProgress ? "" : "none";
  progress.setAttribute("stroke-dasharray", `${circumference}`);
  progress.setAttribute("stroke-dashoffset", `${dashOffset}`);

  if (state.photoUrl) {
    photo.style.display = "block";
    photo.style.backgroundImage = `url("${state.photoUrl}")`;
    label.textContent = "";
  } else {
    photo.style.display = "none";
    photo.style.backgroundImage = "";
    label.textContent = state.initials;
  }

  root.style.opacity = `${state.opacity ?? 1}`;

  // Selection wins over the far-from-stage highlight: the producer just
  // clicked this marker, so that's the state they're looking for.
  inner.style.transform = state.isSelected ? "scale(1.15)" : "scale(1)";
  if (state.isSelected) {
    inner.style.boxShadow = `0 0 0 2px ${CREW_MARKER_COLORS.selectedHalo}, 0 0 14px 2px ${CREW_MARKER_COLORS.selectedGlow}`;
  } else if (state.isFarFromStage) {
    inner.style.boxShadow = `0 0 14px 3px ${CREW_MARKER_COLORS.farFromStage}`;
  } else {
    inner.style.boxShadow = "none";
  }
}

/**
 * Grow the marker slightly while hovered, unless it's already scaled up
 * because it is selected. Assigned as properties (not addEventListener)
 * so re-applying on a reused marker replaces the old handler instead of
 * stacking a new one on every poll.
 */
export function attachCrewMarkerHover(root: HTMLElement, isSelected: boolean): void {
  const inner = root.querySelector<HTMLElement>(`.${CLASS.inner}`);
  if (!inner) return;
  root.onmouseenter = () => {
    if (!isSelected) inner.style.transform = "scale(1.1)";
  };
  root.onmouseleave = () => {
    inner.style.transform = isSelected ? "scale(1.15)" : "scale(1)";
  };
}
